import { api } from '@/api';
import type { ArrayDetectorLayoutDto } from '@/api/generator';
import { ApiError } from '@/errorHandling';
import { computedAsync, useMemoize } from '@vueuse/core';
import { computed, type Ref } from 'vue';

export type ChamberDot = { id: number; x: number; y: number };

export type FieldFrame = { half: number; x: number; size: number; label: string };

// viewBox padding (mm) on each side, so edge chambers (which can sit exactly on
// the extent boundary) don't touch the plate edge
const PADDING = 5;

// Simulated devices carry the backend's simulator suffix on their type number
// (e.g. "T10044.SIMU") but share the geometry of the real model, which is what
// the layout endpoint is keyed on.
export const SIMULATOR_SUFFIX = '.SIMU';

export function toLayoutTypeNumber(typeNumber: string): string {
  return typeNumber.endsWith(SIMULATOR_SUFFIX)
    ? typeNumber.slice(0, -SIMULATOR_SUFFIX.length)
    : typeNumber;
}

// The map degrades to an empty plate for every failure, so none of them is
// worth a notification. A 404 is the expected "no layout data for this model"
// state and stays silent; anything else is noted in the console only.
function swallowLayoutErrorHandler(error: Error) {
  if (error.name !== ApiError.notFound) {
    console.warn('Detector layout request failed; the chamber map stays empty.', error);
  }
}

// One request per detector model per session, shared across all component
// instances (the calibration tab mounts one modal per row). Every outcome is
// cached, including failures - retrying per mount would fan one backend
// problem out into a request per row.
const fetchLayout = useMemoize(async (typeNumber: string) => {
  const { result } = await api.qaEquipmentApi.getArrayDetectorLayout({ typeNumber }, [
    swallowLayoutErrorHandler,
  ]);
  return result;
});

// Detector geometry comes from the backend, resolved by device type number
// (e.g. "T10043"): chamber positions in mm, origin at the detector center,
// +y towards the gantry.
export function useDetectorLayout(typeNumber: Ref<string | undefined>) {
  const layout = computedAsync<ArrayDetectorLayoutDto | undefined>(
    () => (typeNumber.value ? fetchLayout(toLayoutTypeNumber(typeNumber.value)) : undefined),
    undefined,
  );

  // SVG viewBox edge length: layout extent (mm) plus padding on each side
  const extent = computed(() => layout.value?.extent?.width ?? 300);
  const size = computed(() => extent.value + 2 * PADDING);

  // Layout mm -> SVG coordinates: shift the center to size/2 and flip y
  // (physical +y points towards the gantry, i.e. up; SVG y grows downwards).
  const dots = computed<ChamberDot[]>(() =>
    (layout.value?.chambers ?? []).flatMap((chamber) =>
      chamber.id === undefined || chamber.x === undefined || chamber.y === undefined
        ? []
        : [
            {
              id: chamber.id,
              x: size.value / 2 + chamber.x,
              y: size.value / 2 - chamber.y,
            },
          ],
    ),
  );

  // Ids the layout doesn't know (data mismatch or missing layout) get no
  // dot; consumers flag them instead of silently swallowing the difference.
  const dotIds = computed(() => new Set(dots.value.map((dot) => dot.id)));

  // False while loading, after a failed fetch, and for layouts without
  // chambers — every case where the map would be an empty plate.
  const hasLayout = computed(() => dots.value.length > 0);

  // Field-size frames of the detector plate (mm half-edge -> SVG rect + label)
  const frames = computed<FieldFrame[]>(() =>
    (layout.value?.frames ?? []).map((half) => ({
      half,
      x: size.value / 2 - half,
      size: half * 2,
      label: `${(half * 2) / 10} x ${(half * 2) / 10} cm`,
    })),
  );

  return { pad: PADDING, size, dots, dotIds, frames, hasLayout };
}
